// Categoria Financeira (issue #56): a classificação de uma Conta a Pagar ou a
// Receber, com subcategoria. Criável e editável pelo usuário — não fixa em
// código, como manda o princípio transversal do CONTEXT.md.
//
// A exceção é a Categoria de **sistema** "Faturamento": ela existe porque a
// Fatura emitida gera uma Conta a Receber (ADR-0011) e precisa de uma casa
// estável para classificá-la. Por isso não pode ser editada nem removida.

import { newId, type Id } from "./ids.ts";
import {
  APLICACOES_CATEGORIA,
  type AplicacaoCategoria,
  type CategoriaFinanceira,
  type Snapshot,
} from "./snapshot.ts";

export type { CategoriaFinanceira, AplicacaoCategoria };
export { APLICACOES_CATEGORIA };

/** Nome da Categoria de sistema que a Fatura usa (ADR-0011). */
export const CATEGORIA_FATURAMENTO = "Faturamento";

export interface DadosCategoriaFinanceira {
  nome: string;
  subcategoria: string;
  aplicaA: AplicacaoCategoria;
}

export class ErroCategoriaFinanceira extends Error {}

/** Duas Categorias são a mesma quando nome **e** subcategoria coincidem. */
function mesmaChave(
  a: { nome: string; subcategoria: string },
  b: { nome: string; subcategoria: string },
): boolean {
  return (
    a.nome.toLowerCase() === b.nome.toLowerCase() &&
    a.subcategoria.toLowerCase() === b.subcategoria.toLowerCase()
  );
}

function validar(
  dados: DadosCategoriaFinanceira,
  snapshot: Snapshot,
  ignorarId?: Id,
): Omit<CategoriaFinanceira, "id" | "ativo" | "sistema"> {
  const nome = dados.nome.trim();
  if (nome === "") {
    throw new ErroCategoriaFinanceira("Informe o nome da categoria financeira.");
  }
  const subcategoria = dados.subcategoria.trim();
  if (!(APLICACOES_CATEGORIA as readonly string[]).includes(dados.aplicaA)) {
    throw new ErroCategoriaFinanceira(
      "Escolha se a categoria se aplica a pagar, receber ou ambos.",
    );
  }
  const repetida = snapshot.categoriasFinanceiras.find(
    (c) => c.id !== ignorarId && mesmaChave(c, { nome, subcategoria }),
  );
  if (repetida) {
    throw new ErroCategoriaFinanceira(
      subcategoria === ""
        ? `Já existe a categoria financeira "${nome}".`
        : `Já existe "${nome} › ${subcategoria}".`,
    );
  }
  return { nome, subcategoria, aplicaA: dados.aplicaA };
}

function exigir(snapshot: Snapshot, id: Id): CategoriaFinanceira {
  const c = snapshot.categoriasFinanceiras.find((x) => x.id === id);
  if (!c) throw new ErroCategoriaFinanceira("Categoria financeira não encontrada.");
  return c;
}

export function criarCategoriaFinanceira(
  snapshot: Snapshot,
  dados: DadosCategoriaFinanceira,
  /** Id gerado no cliente (ADR-0003). Omitido = o domínio gera. */
  id: Id = newId(),
): Snapshot {
  const c: CategoriaFinanceira = {
    id,
    ...validar(dados, snapshot),
    sistema: false,
    ativo: true,
  };
  return {
    ...snapshot,
    categoriasFinanceiras: [...snapshot.categoriasFinanceiras, c],
  };
}

export function editarCategoriaFinanceira(
  snapshot: Snapshot,
  id: Id,
  dados: DadosCategoriaFinanceira,
): Snapshot {
  const atual = exigir(snapshot, id);
  if (atual.sistema) {
    throw new ErroCategoriaFinanceira(
      `A Categoria "${atual.nome}" é do sistema e não pode ser editada.`,
    );
  }
  const campos = validar(dados, snapshot, id);
  return {
    ...snapshot,
    categoriasFinanceiras: snapshot.categoriasFinanceiras.map((c) =>
      c.id === id ? { ...c, ...campos } : c,
    ),
  };
}

export function definirAtivoCategoriaFinanceira(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  const atual = exigir(snapshot, id);
  if (atual.sistema && !ativo) {
    throw new ErroCategoriaFinanceira(
      `A Categoria "${atual.nome}" é do sistema e não pode ser desativada.`,
    );
  }
  return {
    ...snapshot,
    categoriasFinanceiras: snapshot.categoriasFinanceiras.map((c) =>
      c.id === id ? { ...c, ativo } : c,
    ),
  };
}

/**
 * Exclusão de vez. Só passa se a Categoria não estiver em uso — inativar é o
 * caminho para uma que já classificou alguma Conta.
 */
export function excluirCategoriaFinanceira(snapshot: Snapshot, id: Id): Snapshot {
  const atual = exigir(snapshot, id);
  if (atual.sistema) {
    throw new ErroCategoriaFinanceira(
      `A Categoria "${atual.nome}" é do sistema e não pode ser removida.`,
    );
  }
  if (categoriaFinanceiraEmUso(snapshot, id)) {
    throw new ErroCategoriaFinanceira(
      `"${atual.nome}" está em uso por alguma conta. Desative-a em vez de remover.`,
    );
  }
  return {
    ...snapshot,
    categoriasFinanceiras: snapshot.categoriasFinanceiras.filter((c) => c.id !== id),
  };
}

/** Alguma Conta usa esta Categoria? */
export function categoriaFinanceiraEmUso(snapshot: Snapshot, id: Id): boolean {
  return snapshot.contas.some((c) => c.categoriaId === id);
}

/** As Categorias que servem a uma direção, ativas, em ordem de exibição. */
export function categoriasFinanceirasPara(
  snapshot: Snapshot,
  direcao: "pagar" | "receber",
): CategoriaFinanceira[] {
  return snapshot.categoriasFinanceiras
    .filter((c) => c.ativo && (c.aplicaA === direcao || c.aplicaA === "ambos"))
    .sort(
      (a, b) =>
        a.nome.localeCompare(b.nome, "pt-BR") ||
        a.subcategoria.localeCompare(b.subcategoria, "pt-BR"),
    );
}

/** Rótulo de exibição: "Nome" ou "Nome › Subcategoria". */
export function rotuloCategoriaFinanceira(c: CategoriaFinanceira): string {
  return c.subcategoria === "" ? c.nome : `${c.nome} › ${c.subcategoria}`;
}

/** A Categoria de sistema que a Fatura usa. `undefined` num banco não semeado. */
export function categoriaFaturamento(
  snapshot: Snapshot,
): CategoriaFinanceira | undefined {
  return snapshot.categoriasFinanceiras.find(
    (c) => c.sistema && c.nome === CATEGORIA_FATURAMENTO,
  );
}

/**
 * Garante que a Categoria de sistema "Faturamento" existe, criando-a se
 * preciso. Idempotente.
 *
 * Ela é **invariante do domínio**, não escolha do usuário: a Fatura emitida
 * precisa dela para classificar a Conta a Receber que gera (ADR-0011). Por
 * isso a emissão a garante em vez de falhar — falhar transformaria um detalhe
 * de instalação num erro na cara de quem está faturando.
 */
export function garantirCategoriaFaturamento(
  snapshot: Snapshot,
  id: Id = newId(),
): Snapshot {
  if (categoriaFaturamento(snapshot)) return snapshot;
  const c: CategoriaFinanceira = {
    id,
    nome: CATEGORIA_FATURAMENTO,
    subcategoria: "",
    aplicaA: "receber",
    sistema: true,
    ativo: true,
  };
  return {
    ...snapshot,
    categoriasFinanceiras: [...snapshot.categoriasFinanceiras, c],
  };
}
